import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SupportClient, {
    CreateSupportRequest,
    SupportRequestStatus,
    SupportRequestSummary,
    SupportRequestType,
} from '../apiClients/SupportClient';
import BusySpinner from '../components/BusySpinner';
import { useErrorDisplay } from '../components/ErrorDisplay';
import PageTitle from '../components/PageTitle';
import PanelSegment from '../components/PanelSegment';
import {
    ActivityTimeline,
    PriorityChip,
    RequestTypeIcon,
    StatusChip,
} from '../components/support/SupportRequestVisuals';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import useViewportMode from '../hooks/useViewportMode';
import CreateSupportRequestDialog from '../components/support/CreateSupportRequestDialog';

type StatusFilter = 'Open' | 'Closed' | 'All';

const closedStatuses: SupportRequestStatus[] = ['Resolved', 'NotPlanned', 'Duplicate', 'Closed'];

const ClientRequestsPage = () => {
    const history = useHistory();
    const location = useLocation();
    const { isPhone, isTablet, isPortrait } = useViewportMode();
    const compactDrillIn = isPhone || (isTablet && isPortrait);
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [requests, setRequests] = useState<SupportRequestSummary[]>([]);
    const [selected, setSelected] = useState<SupportRequestSummary>();
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('Open');
    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [createType, setCreateType] = useState<SupportRequestType>('Issue');
    const [reply, setReply] = useState('');

    const loadRequests = async (signal?: AbortSignal) => {
        try {
            setBusy(true);
            const data = await new SupportClient().GetRequests(signal);
            const sorted = [...data].sort((a, b) => dayjs(b.LastUpdateDate).valueOf() - dayjs(a.LastUpdateDate).valueOf());
            setRequests(sorted);
            const selectedId = selected?.Id && sorted.some(item => item.Id === selected.Id)
                ? selected.Id
                : compactDrillIn ? undefined : sorted[0]?.Id;
            setSelected(selectedId ? await new SupportClient().GetRequest(selectedId, signal) : undefined);
        } catch (error) {
            if (!signal?.aborted)
                setError(error, 'Unable to load requests');
        } finally {
            if (!signal?.aborted)
                setBusy(false);
        }
    };

    useEffect(() => {
        const controller = new AbortController();
        loadRequests(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const requestedType = new URLSearchParams(location.search).get('new');
        if (requestedType !== 'Issue' && requestedType !== 'FeatureRequest')
            return;

        setCreateType(requestedType);
        setCreateOpen(true);
        history.replace('/ClientRequests');
    }, [history, location.search]);

    const filtered = useMemo(() => requests.filter(request => {
        const isClosed = closedStatuses.includes(request.Status);
        const matchesStatus = statusFilter === 'All' || (statusFilter === 'Closed' ? isClosed : !isClosed);
        const search = query.trim().toLowerCase();
        return matchesStatus && (!search || `${request.Number} ${request.Title} ${request.Description}`.toLowerCase().includes(search));
    }), [query, requests, statusFilter]);

    const openCount = requests.filter(item => !closedStatuses.includes(item.Status)).length;
    const needsInfoCount = requests.filter(item => item.Status === 'WaitingForClient').length;
    const featureCount = requests.filter(item => item.Type === 'FeatureRequest').length;

    const createRequest = async (request: CreateSupportRequest) => {
        try {
            setBusy(true);
            const created = await new SupportClient().CreateRequest(request);
            setCreateOpen(false);
            await loadRequests();
            setSelected(created);
        } catch (error) {
            setError(error, 'Unable to create request');
        } finally {
            setBusy(false);
        }
    };

    const refreshSelected = async (id: string) => {
        const updated = await new SupportClient().GetRequest(id);
        setSelected(updated);
        setRequests(items => items.map(item => item.Id === updated.Id ? updated : item));
    };

    const addReply = async () => {
        if (!selected || !reply.trim()) return;
        try {
            setBusy(true);
            const updated = await new SupportClient().AddComment(selected.Id, reply.trim(), selected.RowVersion);
            setReply('');
            setSelected(updated);
            setRequests(items => items.map(item => item.Id === updated.Id ? updated : item));
        } catch (error) {
            setError(error, 'Unable to post reply');
        } finally {
            setBusy(false);
        }
    };

    const changeClosedState = async () => {
        if (!selected) return;
        try {
            setBusy(true);
            const isClosed = closedStatuses.includes(selected.Status);
            const updated = isClosed
                ? await new SupportClient().ReopenRequest(selected.Id, selected.RowVersion)
                : await new SupportClient().CloseRequest(selected.Id, selected.RowVersion);
            setSelected(updated);
            await loadRequests();
        } catch (error) {
            setError(error, 'Unable to update request');
        } finally {
            setBusy(false);
        }
    };

    return <>
        <ErrorDisplay />
        <BusySpinner component={Paper} busy={busy}>
            <PanelSegment
                titleContent={<Box>
                    <PageTitle title="Requests" />
                    <Typography color="text.secondary">Track support issues and shape what Synergist Pro builds next.</Typography>
                </Box>}
                toolbar={<Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setCreateType('Issue'); setCreateOpen(true); }}>New request</Button>}
                fillHeight>
                <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                        <SummaryCard icon={<ConfirmationNumberOutlinedIcon />} label="Open requests" value={openCount} color="primary.main" />
                        <SummaryCard icon={<ChatBubbleOutlineRoundedIcon />} label="Waiting on you" value={needsInfoCount} color="warning.main" />
                        <SummaryCard icon={<CheckCircleOutlineRoundedIcon />} label="Ideas submitted" value={featureCount} color="info.main" />
                    </Grid>

                    {needsInfoCount > 0 && <Alert severity="warning" sx={{ mb: 2 }}>One or more requests need additional information from your team.</Alert>}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5} lg={4} sx={{ display: compactDrillIn && selected ? 'none' : 'block' }}>
                            <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                                <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, value) => value && setStatusFilter(value)}>
                                        <ToggleButton value="Open">Open</ToggleButton>
                                        <ToggleButton value="Closed">Closed</ToggleButton>
                                        <ToggleButton value="All">All</ToggleButton>
                                    </ToggleButtonGroup>
                                    <TextField size="small" placeholder="Search requests" value={query} onChange={event => setQuery(event.target.value)} sx={{ flex: 1, minWidth: 150 }} />
                                </Box>
                                <Divider />
                                <Stack sx={{ maxHeight: compactDrillIn ? 'none' : 'calc(100vh - 390px)', overflowY: compactDrillIn ? 'visible' : 'auto' }}>
                                    {filtered.length === 0 && <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography fontWeight={700}>No matching requests</Typography>
                                        <Typography variant="body2" color="text.secondary">Create a request or adjust the current filters.</Typography>
                                    </Box>}
                                    {filtered.map(request => <RequestListItem
                                        key={request.Id}
                                        request={request}
                                        selected={selected?.Id === request.Id}
                                        onClick={() => refreshSelected(request.Id)}
                                    />)}
                                </Stack>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={7} lg={8} sx={{ display: compactDrillIn && !selected ? 'none' : 'block' }}>
                            {selected
                                ? <RequestDetail
                                    request={selected}
                                    reply={reply}
                                    setReply={setReply}
                                    addReply={addReply}
                                    changeClosedState={changeClosedState}
                                    refresh={() => refreshSelected(selected.Id)}
                                    onBack={compactDrillIn ? () => setSelected(undefined) : undefined}
                                />
                                : <Card variant="outlined"><CardContent sx={{ py: 10, textAlign: 'center' }}><Typography color="text.secondary">Select a request to view its details.</Typography></CardContent></Card>}
                        </Grid>
                    </Grid>
                </Box>
            </PanelSegment>
        </BusySpinner>
        <CreateSupportRequestDialog
            open={createOpen}
            initialType={createType}
            routeId="support.requests"
            busy={busy}
            onClose={() => setCreateOpen(false)}
            onSubmit={createRequest}
        />
    </>;
};

const SummaryCard = ({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: number; color: string }) =>
    <Grid item xs={12} sm={4}>
        <Card variant="outlined" sx={{ height: '100%', background: theme => `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.action.hover})` }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'grid', placeItems: 'center', width: 48, height: 48, borderRadius: 2.5, color: '#fff', bgcolor: color }}>{icon}</Box>
                <Box><Typography variant="h3">{value}</Typography><Typography color="text.secondary">{label}</Typography></Box>
            </CardContent>
        </Card>
    </Grid>;

const RequestListItem = ({ request, selected, onClick }: { request: SupportRequestSummary; selected: boolean; onClick: () => void }) =>
    <CardActionArea onClick={onClick} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: selected ? 'action.selected' : 'transparent' }}>
        <Box sx={{ p: 2, display: 'flex', gap: 1.5 }}>
            <RequestTypeIcon type={request.Type} small />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" color="text.secondary">REQ-{request.Number}</Typography>
                    <Typography variant="caption" color="text.secondary">{dayjs(request.LastUpdateDate).format('MMM D')}</Typography>
                </Stack>
                <Typography fontWeight={750} noWrap>{request.Title}</Typography>
                <Stack direction="row" gap={0.75} sx={{ mt: 1 }}><StatusChip status={request.Status} /><PriorityChip priority={request.Priority} /></Stack>
            </Box>
        </Box>
    </CardActionArea>;

const RequestDetail = ({ request, reply, setReply, addReply, changeClosedState, refresh, onBack }: {
    request: SupportRequestSummary;
    reply: string;
    setReply: (value: string) => void;
    addReply: () => void;
    changeClosedState: () => void;
    refresh: () => void;
    onBack?: () => void;
}) => {
    const closed = closedStatuses.includes(request.Status);
    return <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {onBack && (
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack} sx={{ minHeight: 44, mb: 1 }}>
                    All requests
                </Button>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <RequestTypeIcon type={request.Type} />
                    <Box>
                        <Typography variant="overline" color="text.secondary">REQ-{request.Number} · {request.Type === 'Issue' ? 'Issue' : 'Feature request'}</Typography>
                        <Typography variant="h4">{request.Title}</Typography>
                        <Typography variant="body2" color="text.secondary">Created {dayjs(request.CreatedDate).format('MMMM D, YYYY')}</Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap"><StatusChip status={request.Status} /><PriorityChip priority={request.Priority} /></Stack>
            </Stack>
            <Typography sx={{ mt: 3, whiteSpace: 'pre-wrap' }}>{request.Description}</Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>Activity</Typography>
            <ActivityTimeline activity={request.Activity} />
            <Divider sx={{ my: 3 }} />
            <TextField fullWidth multiline minRows={3} label="Reply to the Synergist Pro team" value={reply} onChange={event => setReply(event.target.value)} />
            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 1.5 }} flexWrap="wrap">
                <Button color={closed ? 'primary' : 'inherit'} onClick={changeClosedState}>{closed ? 'Reopen request' : 'Close request'}</Button>
                <Stack direction="row" spacing={1}><Button startIcon={<RefreshRoundedIcon />} onClick={refresh}>Refresh</Button><Button variant="contained" disabled={!reply.trim()} onClick={addReply}>Post reply</Button></Stack>
            </Stack>
        </CardContent>
    </Card>;
};

export default ClientRequestsPage;
